import React, { useState } from 'react';
import { Mail, Send, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const Newsletter = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [message, setMessage] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [loadingNewsletter, setLoadingNewsletter] = useState(false);
  const [loadingContact, setLoadingContact] = useState(false);
  const { toast } = useToast();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    
    setLoadingNewsletter(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubscribed(true);
        toast({
          title: '¡Suscripción exitosa!',
          description: 'Recibirás las últimas novedades de Blox en tu correo.',
        });
        setNewsletterEmail('');
        setTimeout(() => setSubscribed(false), 3000);
      } else {
        throw new Error(data.detail || 'Error al suscribirse');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo procesar la suscripción.',
        variant: 'destructive'
      });
    } finally {
      setLoadingNewsletter(false);
    }
  };

  const handleContact = async (e) => {
    e.preventDefault();
    if (!name || !contactEmail || !message) return;
    
    setLoadingContact(true);
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email: contactEmail, message })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: '¡Mensaje enviado!',
          description: 'Te responderemos a la brevedad.',
        });
        setName('');
        setContactEmail('');
        setMessage('');
      } else {
        throw new Error(data.detail || 'Error al enviar mensaje');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo enviar el mensaje.',
        variant: 'destructive'
      });
    } finally {
      setLoadingContact(false);
    }
  };

  return (
    <section className="py-24 bg-slate-900 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/5 to-transparent"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Newsletter */}
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-xl flex items-center justify-center">
                <Mail className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-2xl font-bold text-white">Newsletter</h3>
            </div>
            
            <p className="text-slate-400 mb-6 leading-relaxed">
              Mantente al día con las últimas actualizaciones, tutoriales y novedades de Blox.
            </p>

            <form onSubmit={handleSubscribe} className="space-y-4">
              <Input
                type="email"
                placeholder="tu@email.com"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                required
                disabled={loadingNewsletter}
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 h-12"
              />
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-slate-950 font-semibold h-12"
                disabled={subscribed || loadingNewsletter}
              >
                {loadingNewsletter ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : subscribed ? (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    ¡Suscrito!
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Suscribirse
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Contact Form */}
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-xl flex items-center justify-center">
                <Send className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-2xl font-bold text-white">Contáctanos</h3>
            </div>
            
            <p className="text-slate-400 mb-6 leading-relaxed">
              ¿Tienes preguntas o sugerencias? Nos encantaría escucharte.
            </p>

            <form onSubmit={handleContact} className="space-y-4">
              <Input
                type="text"
                placeholder="Tu nombre"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={loadingContact}
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 h-12"
              />
              <Input
                type="email"
                placeholder="tu@email.com"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                required
                disabled={loadingContact}
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 h-12"
              />
              <Textarea
                placeholder="Tu mensaje..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                disabled={loadingContact}
                rows={4}
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 resize-none"
              />
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-slate-950 font-semibold h-12"
                disabled={loadingContact}
              >
                {loadingContact ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Enviar Mensaje
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;